import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Input, Spinner } from "reactstrap";
import RecommendationRow from "./RecommendationRow";
import {
	EMPTY_RECOMMENDATION_FILTERS,
	catLabel,
	rowKey,
	uniqueAuditIds,
	uniqueCategories,
} from "./recommendationGridUtils";
import { formatWritingAuditOption } from "../writingAssistantUtils";

export default function RecommendationsGrid({
	items,
	total,
	skip,
	page,
	pageSize,
	loading,
	actionLoading,
	filters = EMPTY_RECOMMENDATION_FILTERS,
	auditOptions = [],
	onFiltersChange,
	onClearFilters,
	onPageChange,
	onPageSizeChange,
	onView,
	onGenerate,
}) {
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(() => new Set());
	const [knownCategories, setKnownCategories] = useState([]);
	const [knownAuditIds, setKnownAuditIds] = useState([]);

	const categoriesFromItems = useMemo(() => uniqueCategories(items), [items]);
	const categories = useMemo(() => {
		const set = new Set([...knownCategories, ...categoriesFromItems]);
		return Array.from(set).sort();
	}, [knownCategories, categoriesFromItems]);

	const auditsFromItems = useMemo(() => uniqueAuditIds(items), [items]);
	const auditSelectOptions = useMemo(() => {
		const byId = new Map();
		(auditOptions || []).forEach((audit) => {
			if (audit?.id == null || audit.id === "") return;
			byId.set(String(audit.id), {
				id: String(audit.id),
				label: formatWritingAuditOption(audit),
			});
		});
		[...knownAuditIds, ...auditsFromItems].forEach((id) => {
			const key = String(id);
			if (!byId.has(key)) {
				byId.set(key, { id: key, label: key });
			}
		});
		return Array.from(byId.values()).sort((a, b) =>
			String(a.label).localeCompare(String(b.label))
		);
	}, [auditOptions, knownAuditIds, auditsFromItems]);

	useEffect(() => {
		if (categoriesFromItems.length === 0) return;
		setKnownCategories((prev) => {
			const set = new Set([...prev, ...categoriesFromItems]);
			if (set.size === prev.length) return prev;
			return Array.from(set).sort();
		});
	}, [categoriesFromItems]);

	useEffect(() => {
		if (auditsFromItems.length === 0) return;
		setKnownAuditIds((prev) => {
			const set = new Set([...prev, ...auditsFromItems]);
			if (set.size === prev.length) return prev;
			return Array.from(set).sort();
		});
	}, [auditsFromItems]);

	const rangeStart = total === 0 ? 0 : skip + 1;
	const rangeEnd = skip + (items?.length || 0);
	const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 20)));

	const setFilter = (key, value) => {
		onFiltersChange?.({ ...filters, [key]: value });
	};

	const toggleExpand = (id) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	return (
		<div className="rec-grid">
			<div className="pageHeader mb-3 d-flex flex-wrap align-items-start justify-content-between gap-2">
				<div>
					<h2 className="pageTitle mb-1 rec-grid__page-title">AEO Recommendations</h2>
				</div>
				<Button
					color="primary"
					size="sm"
					onClick={() => navigate("/aivisibility/writing-assistance")}
				>
					Go to Content Studio
				</Button>
			</div>

			<Card className="rec-grid__filter-card border-0 shadow-sm mb-3">
				<CardBody className="py-3">
					<div className="rec-grid__filters-row">
						<div className="rec-grid__filter-field rec-grid__filter-field--title">
							<div className="rec-grid__filter-label">Title</div>
							<Input
								bsSize="sm"
								placeholder="Search recommendation title..."
								value={filters.title}
								onChange={(e) => setFilter("title", e.target.value)}
							/>
						</div>
						<div className="rec-grid__filter-field rec-grid__filter-field--wide">
							<div className="rec-grid__filter-label">Audit</div>
							<Input
								type="select"
								bsSize="sm"
								value={filters.auditId || ""}
								onChange={(e) => setFilter("auditId", e.target.value)}
							>
								<option value="">All audits</option>
								{auditSelectOptions.map((a) => (
									<option key={a.id} value={a.id}>
										{a.label}
									</option>
								))}
							</Input>
						</div>
						<div className="rec-grid__filter-field rec-grid__filter-field--title">
							<div className="rec-grid__filter-label">Affected page</div>
							<Input
								bsSize="sm"
								placeholder="Search page title or URL..."
								value={filters.url || ""}
								onChange={(e) => setFilter("url", e.target.value)}
							/>
						</div>
						<div className="rec-grid__filter-field">
							<div className="rec-grid__filter-label">Category</div>
							<Input
								type="select"
								bsSize="sm"
								value={filters.category}
								onChange={(e) => setFilter("category", e.target.value)}
							>
								<option value="">All categories</option>
								{categories.map((c) => (
									<option key={c} value={c}>
										{catLabel(c)}
									</option>
								))}
							</Input>
						</div>
						<div className="rec-grid__filter-field">
							<div className="rec-grid__filter-label">Severity</div>
							<Input
								type="select"
								bsSize="sm"
								value={filters.severity}
								onChange={(e) => setFilter("severity", e.target.value)}
							>
								<option value="">All severities</option>
								<option value="high">High</option>
								<option value="medium">Medium</option>
								<option value="__none__">None</option>
							</Input>
						</div>
						<div className="rec-grid__filter-field">
							<div className="rec-grid__filter-label">Impact</div>
							<Input
								type="select"
								bsSize="sm"
								value={filters.impact}
								onChange={(e) => setFilter("impact", e.target.value)}
							>
								<option value="">All impact</option>
								<option value="high">High</option>
								<option value="medium">Medium</option>
							</Input>
						</div>
						<div className="rec-grid__filter-field">
							<div className="rec-grid__filter-label">Job status</div>
							<Input
								type="select"
								bsSize="sm"
								value={filters.jobstatus}
								onChange={(e) => setFilter("jobstatus", e.target.value)}
							>
								<option value="">All status</option>
								<option value="none">None</option>
								<option value="pending">Pending</option>
								<option value="in_progress">In progress</option>
								<option value="completed">Completed</option>
								<option value="failed">Failed</option>
							</Input>
						</div>
						<div className="rec-grid__filter-actions">
							<Button
								color="light"
								size="sm"
								className="border"
								onClick={onClearFilters}
							>
								<i className="fas fa-arrow-rotate-left me-1" />
								Clear filters
							</Button>
						</div>
					</div>
				</CardBody>
			</Card>

			<Card className="border-0 shadow-sm">
				{loading ? (
					<div className="text-center py-5">
						<Spinner size="sm" />
					</div>
				) : (
					<>
						<div className="table-responsive">
							<table className="table rec-grid__table mb-0">
								<thead>
									<tr>
										<th style={{ width: 26 }} />
										<th>Recommendation</th>
										<th>Category</th>
										<th>Severity</th>
										<th>Impact</th>
										<th>Effort</th>
										<th>Affected page</th>
										<th>Job status</th>
										<th>Deployment status</th>
										<th>Validation status</th>
										<th>Created</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{items.map((item, index) => {
										const id = rowKey(item, index);
										return (
											<RecommendationRow
												key={id}
												item={item}
												rowId={id}
												expanded={expanded.has(id)}
												actionLoading={actionLoading}
												onToggleExpand={toggleExpand}
												onView={onView}
												onGenerate={onGenerate}
											/>
										);
									})}
								</tbody>
							</table>
						</div>
						{items.length === 0 ? (
							<div className="rec-grid__empty">
								<i className="far fa-folder-open fs-2 mb-2 d-block" />
								No recommendations match these filters.
							</div>
						) : null}
					</>
				)}
			</Card>

			<div className="d-flex justify-content-between align-items-center mt-3 rec-grid__footer flex-wrap gap-2">
				<div>
					Showing <strong>{rangeStart} – {rangeEnd}</strong> of{" "}
					<strong>{total}</strong> recommendations
					{" · "}
					<select
						className="form-select form-select-sm d-inline-block rec-grid__page-size"
						value={pageSize}
						onChange={(e) => onPageSizeChange(Number(e.target.value))}
					>
						<option value={20}>20 / page</option>
						<option value={50}>50 / page</option>
						<option value={100}>100 / page</option>
					</select>
				</div>
				<nav aria-label="Recommendations pagination">
					<ul className="pagination pagination-sm mb-0">
						<li className={`page-item${page <= 1 ? " disabled" : ""}`}>
							<button
								type="button"
								className="page-link"
								disabled={page <= 1}
								onClick={() => onPageChange(page - 1)}
							>
								«
							</button>
						</li>
						{Array.from({ length: totalPages }, (_, i) => i + 1)
							.filter((p) => {
								if (totalPages <= 7) return true;
								return (
									p === 1 ||
									p === totalPages ||
									Math.abs(p - page) <= 1
								);
							})
							.map((p, idx, arr) => {
								const prev = arr[idx - 1];
								const showEllipsis = prev != null && p - prev > 1;
								return (
									<React.Fragment key={p}>
										{showEllipsis ? (
											<li className="page-item disabled">
												<span className="page-link">…</span>
											</li>
										) : null}
										<li className={`page-item${p === page ? " active" : ""}`}>
											<button
												type="button"
												className="page-link"
												onClick={() => onPageChange(p)}
											>
												{p}
											</button>
										</li>
									</React.Fragment>
								);
							})}
						<li className={`page-item${page >= totalPages ? " disabled" : ""}`}>
							<button
								type="button"
								className="page-link"
								disabled={page >= totalPages}
								onClick={() => onPageChange(page + 1)}
							>
								»
							</button>
						</li>
					</ul>
				</nav>
			</div>
		</div>
	);
}
